import { Col, Row } from "antd";
import TableVelocity from "components/assets/utilities/TableVelocity";
import { QAAlertsColumns } from "components/pages/Result/columns";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { LoadJobAction } from "redux/actions/LoadJob";
import { LoadResultAction } from "redux/actions/LoadResult";
import { SaveObjectAction } from "redux/actions/SaveObject";
import QA from "redux/classes/QA";
import Output from "redux/classes/Output";
import Visualizations from "components/assets/utilities/Visualizations";
import Interactives from "components/assets/utilities/Interactives";
import ExcelVisuals from "components/assets/utilities/ExcelVisuals";
import { ResultWrapper } from "./styles";
import { setOutputIndex, setVisualIndex, setHtmlIndex, setModelIndex, setJobIndex } from "redux/slices/user";
import { requestCompleted, requestFailure } from "redux/slices/app";
import { useSession } from "components/wrappers/SessionProvider";
import OtherVisuals from "components/assets/utilities/OtherVisuals";

const ResultPage = () => {
  const dispatch = useAppDispatch();
  const app = useAppSelector(state => state.app);
  const user = useAppSelector(state => state.user);
  const loadJob = useRef(false);
  const session = useSession();
  const params = useParams();

  // Hard reload (or any direct URL hit) wipes Redux. InitializeSession in the
  // auth flow repopulates `models` and `jobs`, but neither modelIndex nor
  // jobIndex is set from the URL path — Home/Model pages were the only places
  // that set them. So when the user reloads on /:modelName/results/:resultName
  // the indexes stay at -1 and `user.models[-1]` / `user.jobs[-1]` are
  // undefined, crashing every downstream access. Sync them from the URL once
  // the underlying lists have loaded, then let the rest of the page run.
  useEffect(() => {
    if (user.models.length > 0 && user.modelIndex === -1 && params.modelName) {
      const m = user.models.find((mm: any) => mm.alias === params.modelName);
      if (m) dispatch(setModelIndex({ modelID: m.modelID }));
    }
    if (user.jobs.length > 0 && user.jobIndex === -1 && params.resultName) {
      dispatch(setJobIndex({ jobID: params.resultName }));
    }
  }, [user.models.length, user.jobs.length, user.modelIndex, user.jobIndex, params.modelName, params.resultName, dispatch])

  const modelInfo = user.models[user.modelIndex];
  const jobInfo = user.jobs[user.jobIndex]

  const visuals = user.output.filter(output => output.type === 'png')
  const visual = visuals[user.visualIndex]
  const htmlFiles = user.output.filter(output => output.type === 'html')
  const excelFiles = user.output.filter(output => output.type === 'xlsx')
  const outputs = user.output.filter(output => output.type !== 'png' && output.type !== 'html' && output.type !== 'xlsx' && output.type !== 'err')
  const output = outputs[user.outputIndex]
  const error = user.output.filter(output => output.type === 'err')

  useEffect(() => {
    if (!modelInfo || !jobInfo) return;
    dispatch(setOutputIndex({ index: 0 }))
    dispatch(setVisualIndex({ index: 0 }))
    dispatch(setHtmlIndex({ index: 0 }))
    dispatch(LoadJobAction(session, jobInfo.jobID, modelInfo.alias))
    loadJob.current = true;
  }, [modelInfo?.alias, jobInfo?.jobID])

  useEffect(() => {
    if (loadJob.current && modelInfo && jobInfo) {
      const getOutput = async () => {
        await dispatch(LoadResultAction(session, jobInfo.jobID, modelInfo.alias, user.config.outputs, user.jobs[user.jobIndex].status))

        dispatch(requestCompleted());
      }
      getOutput().catch(() => dispatch(requestFailure()));
    }
  }, [user.config])

  const prepareQA = (checks: QA[]) => {
    return checks.map((check, index) => {
      return {
        key: "qa" + index,
        type: check.type,
        name: check.name,
        description: check.description,
        threshold: check.fail_threshold,
        actual: check.actual,
        status: check.status,
        details: check.details
      }
    })
  }

  const prepareColumns = (output: Output) => {
    if (output) {
      switch (output.type) {
        case "err":
          return [{
            key: "column0",
            title: output.path,
            dataIndex: output.path,
            // The `formatted` table className sets `white-space: pre` on cells
            // so the traceback keeps its newlines and indentation. Long lines
            // overflow the viewport and produce a horizontal scroll. Without
            // this wrapper the <p> blocks only paint background to the parent
            // width, so scrolling past the edge would show text on a bare
            // background. inline-block + minWidth:100% makes the painted area
            // grow with the content, not the viewport.
            render: (text: string) => (
              <div style={{ display: "inline-block", minWidth: "100%", backgroundColor: "#ffffff" }}>
                {text.split('\n').map((paragraph: string, index: number) => (
                  <p key={'p' + index} style={{ margin: 0 }}>
                    {paragraph.replace("  ", "\u00A0\u00A0")}
                  </p>
                ))}
              </div>
            )
          }]
      }
    }
  }

  const prepareResult = (output: Output) => {
    if (output) {
      switch (output.type) {
        case "err":
          let result: {[key: string]: string} = {
            key: "column0"
          }
          result[output.path] = atob(output.result).trim()
          return [result]
      }
    }
  }

  const downloadXLSX = (url: string) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = url.split('/').pop()!;
    a.click();
  }

  // Client-side downloads for the QA Alerts and Error sections. Both use data
  // already in Redux state (user.qa / the base64 error.err in user.output) so
  // they don't need a round trip through resultLoader/objectLoader.
  const escapeCSVCell = (value: string) => {
    if (value.indexOf(",") >= 0 || value.indexOf("\"") >= 0 || value.indexOf("\n") >= 0) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  const triggerDownload = (content: string, filename: string, mimeType: string) => {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('download', filename);
    a.setAttribute('href', url);
    a.click();
    URL.revokeObjectURL(url);
  }

  const downloadQA = () => {
    const headers = ["Type", "Category", "Name", "Description", "Fail Threshold", "Warning Threshold", "Actual", "Status", "Details"];
    const rows = user.qa.map(check => [
      check.type, check.category, check.name, check.description,
      check.fail_threshold, check.warn_threshold, check.actual, check.status, check.details,
    ]);
    const csv = [headers, ...rows].map(row => row.map(escapeCSVCell).join(",")).join("\n");
    triggerDownload(csv, `${modelInfo.alias}_QA_${jobInfo.jobID}.csv`, 'text/csv');
  }

  const downloadError = () => {
    if (error.length === 0) return;
    const text = atob(error[0].result);
    triggerDownload(text, `${modelInfo.alias}_${jobInfo.jobID}_error.err`, 'text/plain');
  }

  // Wait until the URL→state sync has resolved both the model and the job.
  // Without this guard, a hard reload on a results URL would access
  // user.models[-1] / user.jobs[-1] and crash before app.loading even toggles.
  if (!modelInfo || !jobInfo) {
    return (
      <ResultWrapper>
        <div className="loader-centered"/>
      </ResultWrapper>
    );
  }

  return (
    <ResultWrapper>
      {app.loading ? <div className="loader-centered"/> :
        <>
          {user.output.filter(output => output.type === 'png').length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24}>
                <Visualizations
                  visuals={visuals}
                  description="Visualizations generated by the model"
                  result
                  download={() => dispatch(SaveObjectAction(session, modelInfo.alias, jobInfo.jobID, visual.path.split('.')[0], visual.type))}
                  extension={visual.type}
                />
              </Col>
            </Row>
          }

          {user.output.filter(output => output.type === 'html').length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24}>
                <Interactives
                  visuals={htmlFiles}
                  description="Interactive visualizations generated by the model"
                />
              </Col>
            </Row>
          }

          {user.output.filter(output => output.type === 'xlsx').length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24}>
                <ExcelVisuals
                  visuals={excelFiles}
                  description="Excel sheets generated by the model"
                  download={downloadXLSX}
                  extension={excelFiles[0].type}
                />
              </Col>
            </Row>
          }

          {user.output.filter(output => output.type !== 'png' && output.type !== 'html' && output.type !== 'xlsx' && output.type !== 'err').length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24}>
                <OtherVisuals
                  visuals={outputs}
                  description="Results generated by the model"
                  session={session}
                  alias={modelInfo.alias}
                  jobID={jobInfo.jobID}
                />
              </Col>
            </Row>
          }

          {user.output.filter(output => output.type === 'err').length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24}>
                <TableVelocity
                  title="Error"
                  type="Error"
                  description="Result files generated by the model"
                  data={prepareResult(error[0])}
                  columns={prepareColumns(error[0])}
                  vh={"600px"}
                  scroll={{ x: "max-content" }}
                  loading={app.loading}
                  options={error}
                  showHeader={false}
                  download={downloadError}
                  extension="err"
                />
              </Col>
            </Row>
          }

          {user.qa.length > 0 &&
            <Row gutter={20} className="mt-24">
              <Col md={24}>
                <TableVelocity
                  title="QA Alerts"
                  description="QA report generated by the model"
                  data={prepareQA(user.qa)}
                  columns={QAAlertsColumns}
                  vh={"400px"}
                  loading={app.loading}
                  download={downloadQA}
                  extension="csv"
                />
              </Col>
            </Row>
          }
        </>
      }
    </ResultWrapper>
  );
};

export default ResultPage;
